import { SITE_URL } from '@/common/constant';
import { Author, Channel, Content, ContentBody } from '@/lib/model';
import { normalizeLanguageCode } from '@/utils/json-ld/convertLanguage';
import { AudioObject, NewsArticle, VideoObject, WithContext } from 'schema-dts';
import { getContentJsonLdSchema } from './getContentJsonLdSchema';
import { convertToISO8601Duration } from '@/utils/json-ld/convertDuration';
export interface GetSchemaStructuredData {
  content?: Content;
  body?: ContentBody;
  author?: Author;
  channelPref: Channel;
}

function extractTextFromTiptap(json: any): string {
  let text = '';

  if (json.text) {
    text += json.text + ' ';
  }

  if (json.content && Array.isArray(json.content)) {
    for (const child of json.content) {
      text += extractTextFromTiptap(child);
    }
  }

  return text.trim();
}

export function getNewsArticleSchemaStructuredData({
  content,
  body,
  author,
  channelPref,
}: GetSchemaStructuredData) {
  const contentData = getContentJsonLdSchema(content, body);
  const { mainImage, mainImageUrl, mainVideo } = contentData;
  const videoObjectDefault = {
    uploadDate: content?.publishedAt || content?.createdAt,
    name: content?.title,
    description: content?.description,
  };
  const structuredData: WithContext<NewsArticle> = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: content?.title,
    name: content?.title,
    inLanguage: normalizeLanguageCode(channelPref?.languageCode),

    articleBody: extractTextFromTiptap(body),
    author: {
      '@type': 'Person',
      name: author?.name || '',
    },
    datePublished: content?.publishedAt || content?.createdAt,
    dateModified: content?.updatedAt || (content?.publishedAt ?? undefined),
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/articles/${content?.slug}`,
    },
    description: content?.description ?? undefined,
    ...(mainImage
      ? {
          image: [mainImageUrl],
        }
      : {}),
    ...(mainVideo && (mainVideo?.url || mainVideo?.media?.url)
      ? {
          video: {
            '@type': 'VideoObject',
            url: mainVideo?.url || mainVideo?.media?.url,
            width: {
              '@type': 'QuantitativeValue',
              unitCode: 'E37',
              value: mainVideo?.media?.width || 0,
            },
            height: {
              '@type': 'QuantitativeValue',
              unitCode: 'E37',
              value: mainVideo?.media?.height || 0,
            },
            caption: mainVideo?.caption || '',
            thumbnailUrl: mainVideo?.media?.thumbnailUrl,
            ...videoObjectDefault,
          } as VideoObject,
        }
      : {}),

    // TODO: due to the ssr issue with associatedMedia, google will not see these embeds.
    // we need to fix ssr embed content to be able to use this.
    // associatedMedia: [
    //   ...twitters.map(embed => ({
    //     '@type': 'SocialMediaPosting',
    //     articleBody: embed.content || '',
    //     sharedContent: {
    //       '@type': 'WebPage',
    //       url: embed?.attrs?.url || embed?.attrs?.src,
    //     },
    //   })),
    //   ...youtubes
    //     .filter(embed => embed?.attrs?.url || embed?.attrs?.src)
    //     .map(embed => ({
    //       '@type': 'VideoObject',
    //       url: embed?.attrs?.url || embed?.attrs?.src,
    //       thumbnailUrl: embed?.attrs?.poster || mainImage?.media?.thumbnailUrl,
    //       ...videoObjectDefault,
    //     })),
    //   ...videos
    //     .filter(video => video.attrs.src)
    //     .map(video => ({
    //       '@type': 'VideoObject',
    //       url: video.attrs.src,
    //       thumbnailUrl: video.attrs.poster || mainImage?.media?.thumbnailUrl,
    //       ...videoObjectDefault,
    //     })),
    //   ...images.map(image => ({
    //     '@type': 'ImageObject',
    //     url: image.attrs.src,
    //     width: image?.attrs?.width || 0,
    //     caption: image?.attrs?.caption || '',
    //   })),
    // ],
  };
  return {
    structuredData,
    contentData,
  };
}

export function getVideoSchemaStructuredData({
  content,
  body,
  author,
  channelPref,
}: GetSchemaStructuredData) {
  const contentData = getContentJsonLdSchema(content, body);
  const { mainImage, mainImageUrl, mainVideo } = contentData;

  const mainVideoUrl = mainVideo?.url || mainVideo?.media?.url;
  const structuredData: WithContext<VideoObject & { articleBody: string }> = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    headline: content?.title,
    name: content?.title,
    inLanguage: normalizeLanguageCode(channelPref?.languageCode),

    articleBody: extractTextFromTiptap(body),
    author: {
      '@type': 'Person',
      name: author?.name || '',
    },
    uploadDate: content?.publishedAt || content?.createdAt,
    datePublished: content?.publishedAt || content?.createdAt,
    dateModified: content?.updatedAt || (content?.publishedAt ?? undefined),
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/articles/${content?.slug}`,
    },
    description: content?.description ?? undefined,
    // unitCode: "E37" → stands for pixel (per UN/CEFACT Common Code).
    ...(mainImage
      ? {
          image: [mainImageUrl],
        }
      : {}),

    thumbnailUrl: mainImageUrl,
    contentUrl: mainVideoUrl ?? undefined,
  };
  return {
    structuredData,
    contentData,
  };
}

export function getAudioSchemaStructuredData({
  content,
  body,
  author,
  channelPref,
}: GetSchemaStructuredData) {
  const contentData = getContentJsonLdSchema(content, body);
  const { mainAudio, mainImageUrl } = contentData;

  const structuredData: WithContext<AudioObject & { articleBody: string }> = {
    '@context': 'https://schema.org',
    '@type': 'AudioObject',

    headline: content?.title,
    name: content?.title,
    inLanguage: normalizeLanguageCode(channelPref?.languageCode),
    articleBody: extractTextFromTiptap(body),
    ...(author?.name
      ? {
          author: {
            '@type': 'Person',
            name: author?.name || '',
          },
        }
      : {}),
    datePublished: content?.publishedAt || content?.createdAt,
    dateModified: content?.updatedAt || (content?.publishedAt ?? undefined),
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/audio/${content?.slug}`,
    },
    description: content?.description ?? undefined,
    url: mainAudio?.url ?? undefined,
    contentUrl: mainAudio?.url ?? undefined,
    duration: convertToISO8601Duration(mainAudio?.media?.duration),
    uploadDate: content?.publishedAt || content?.createdAt,
    encodingFormat: mainAudio?.media?.mimeType ?? undefined,
    thumbnailUrl: mainImageUrl,
    ...(mainImageUrl
      ? {
          image: [mainImageUrl],
        }
      : {}),
    publisher: {
      '@type': 'Organization',
      name: 'TRT Global',
      url: 'https://trt.global',
    },
  };
  return {
    structuredData,
    contentData,
  };
}
